import {
	usePricelistPeriodDeleteMutation,
	usePricelistPeriodListQuery,
} from "@/generated/graphql";
import type { PaginationState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

export const usePricelistPeriodList = (
	{
		page,
		pageSize,
		withCount,
		pricelistID,
	}: {
		page: number;
		pageSize: number;
		withCount: boolean;
		pricelistID?: string;
	} = {
		page: 1,
		pageSize: 10,
		withCount: true,
	},
) => {
	const [paginationState, setPaginationState] = useState<PaginationState>({
		pageIndex: page - 1,
		pageSize: pageSize,
	});
	const [filtersState, setFiltersState] = useState<Record<string, unknown>>({});
	const [, deletePricelistPeriod] = usePricelistPeriodDeleteMutation();

	const options: Parameters<typeof usePricelistPeriodListQuery>[0] = useMemo(
		() => ({
			variables: {
				qop: {
					pagination: {
						page: paginationState.pageIndex + 1,
						pageSize: paginationState.pageSize,
						withCount,
					},
					filters: {
						...filtersState,
						pricelistID,
					},
				},
			},
			requestPolicy: "network-only",
			context: {
				suspense: false,
			},
		}),
		[
			paginationState.pageIndex,
			paginationState.pageSize,
			withCount,
			filtersState,
			pricelistID,
		],
	);

	const [{ data, error, fetching }, fetch] = usePricelistPeriodListQuery(options);

	const load = useCallback(() => {
		fetch();
	}, [fetch]);

	const setPagination = useCallback((paginationState: PaginationState) => {
		setPaginationState(paginationState);
	}, []);

	const setFilters = useCallback((filtersState: Record<string, unknown>) => {
		setFiltersState(filtersState);
		setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
	}, []);

	return {
		data,
		fetching,
		error,
		load,
		paginationState,
		setPagination,
		filtersState,
		setFilters,
		deletePricelistPeriod,
	};
};
